// frontend/src/contexts/SimpleRecordingContext.js
// SIMPLIFIED RECORDING CONTEXT - USES VOICEINPUT INSTEAD OF STREAMING

import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from '../api/axiosConfig';
import { useAuth } from './AuthContext';
import VoiceBackupService from '../services/VoiceBackupService';
import TierLimitService from '../services/TierLimitService';
import { useUserTier } from '../hooks/useUserTier';

const RecordingContext = createContext();

export function useRecording() {
  return useContext(RecordingContext);
}

export function RecordingProvider({ children, onVoiceUploaded }) {
  const { currentUser } = useAuth();

  // RUTHLESS VOICE FIX: Fetch actual user tier from backend
  const { tier: userTier, loading: tierLoading, refetch: refetchTier } = useUserTier();

  // Simple recording state (mimics complex RecordingContext interface)
  const [isRecording, setIsRecording] = useState(false);
  const [isStopping, setIsStopping] = useState(false);  // CRITICAL: Track async stop state
  const [isPaused, setIsPaused] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [enableRealTimeTranscription, setEnableRealTimeTranscription] = useState(true);
  const [lastVoiceId, setLastVoiceId] = useState(null);  // CRITICAL: Store last created voice ID
  const [projectId, setProjectId] = useState(null);  // Project context for voice notes

  // GPT5 FIX: Race guard and duration tracking
  const [pendingUploadToken, setPendingUploadToken] = useState(null);
  const [lastActualDurationSec, setLastActualDurationSec] = useState(null);

  // PHASE B: Bulletproof backup and limits
  const [currentBackupSession, setCurrentBackupSession] = useState(null);
  const [tierLimitsCheck, setTierLimitsCheck] = useState(null)
  
  // Refs for MediaRecorder (simple implementation)
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const currentBackupSessionRef = useRef(null);
  const lastMetadataBackupRef = useRef(0);

  // Realtime transcript ref — synchronous mirror of currentTranscript state.
  // We need this because stopRecording snapshots the transcript for the
  // realtime-POST, and final STT results that arrive after recognition.stop()
  // would otherwise be missed if we read from React state (which only updates
  // on the next render). The ref is updated inside recognition.onresult
  // alongside setCurrentTranscript so it always carries the latest text.
  const realtimeTranscriptRef = useRef('');
  const recognitionEndedRef = useRef(false);

  // KILL SWITCH: Disable chunking on web immediately - fallback to single-blob upload
  const CHUNKED_UPLOADS_ENABLED = false;

  // PHASE 2: Chunked upload state (KEPT FOR LATER - currently disabled via kill switch)
  const recordingIdRef = useRef(null);
  const chunkSequenceRef = useRef(0);
  const uploadedChunksRef = useRef([]);
  const lastCheckpointTimeRef = useRef(0);
  const pendingChunkUploadsRef = useRef(new Set()); // Track in-flight chunk uploads

  // GPT5 STEP 1.5: Fetch backend config on mount to hydrate TierLimitService
  useEffect(() => {
    const fetchVoiceConfig = async () => {
      try {
        const response = await axios.get('/api/settings/public');
        const { voiceRecordingMaxDuration } = response.data;

        if (voiceRecordingMaxDuration) {
          // If backend returns tier map { free, starter, pro, max }
          if (typeof voiceRecordingMaxDuration === 'object') {
            TierLimitService.setVoiceRecordingTierLimits(voiceRecordingMaxDuration);
            console.log('[Recording] Loaded tier-based voice limits from backend');
          } else {
            // Legacy fallback: single number
            TierLimitService.setMaxRecordingDuration(voiceRecordingMaxDuration);
            console.log(`[Recording] Loaded global limit: ${voiceRecordingMaxDuration}s (${voiceRecordingMaxDuration / 60} min)`);
          }
        }
      } catch (error) {
        console.warn('[Recording] Failed to fetch voice config, using default limits:', error);
        // Fail gracefully - TierLimitService will use its default values
      }
    };

    fetchVoiceConfig();
  }, []); // Run once on mount

  // PHASE B: Enhanced recording with bulletproof backup and limits
  const startRecording = async () => {
    if (!currentUser) return;

    // GPT5 REQUIREMENT: Guard against starting recording while tier is loading
    if (tierLoading) {
      console.warn('[Recording] Tier still loading, treating as free tier temporarily');
      // Continue with 'free' tier assumption - better than blocking the user
    }

    try {
      // PHASE B: Check tier limits BEFORE starting recording (GPT5 spec)
      const limitsCheck = TierLimitService.canStartRecording(userTier, 0);
      setTierLimitsCheck(limitsCheck);

      if (!limitsCheck.canRecord) {
        const issues = limitsCheck.issues.map(i => i.message).join('\n');
        alert(`❌ Cannot start recording:\n\n${issues}\n\nUpgrade your plan or try later.`);
        setConnectionStatus('error');
        return;
      }

      // PHASE B: Generate recording session ID
      const recordingSessionId = `simple-${Date.now()}`;

      // CRITICAL FIX: Immediately set ref with ID to ensure it's available
      currentBackupSessionRef.current = { id: recordingSessionId };
      // console.log('[Recording] Starting with sessionId:', recordingSessionId, 'ref:', currentBackupSessionRef.current); // DEBUG: Track session ID lifecycle

      // Set connected status FIRST so UI shows "connected" immediately
      setConnectionStatus('connected');
      setIsRecording(true);
      setSessionId(recordingSessionId);
      setRecordingTime(0);
      setCurrentTranscript('');  // Clear any previous transcript
      realtimeTranscriptRef.current = '';  // Reset sync mirror of currentTranscript
      recognitionEndedRef.current = false;

      // PHASE B: Initialize bulletproof backup session
      const backupSession = await VoiceBackupService.startBackupSession(
        recordingSessionId,
        currentUser.uid,
        {
          enableRealTimeTranscription,
          projectId: projectId
        }
      );
      setCurrentBackupSession(backupSession);
      // Update ref with full backup session data
      currentBackupSessionRef.current = backupSession;
      // console.log('[Recording] Backup session initialized:', backupSession?.id); // DEBUG: Verify backup session creation

      // Start timer with limit enforcement
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1000;

          // PHASE B: Real-time limit checking during recording
          if (tierLimitsCheck) {
            const duringCheck = TierLimitService.checkDuringRecording(
              userTier,
              Math.floor(newTime / 1000),
              audioChunksRef.current.reduce((size, chunk) => size + chunk.size, 0)
            );

            if (duringCheck.shouldStop) {
              // SILENT STOP: No blocking alert, use centralized auto-save helper
              const reasons = duringCheck.stopReasons.map(r => r.message).join('\n');
              console.log(`[TierLimit] Auto-stop triggered: ${reasons}`);

              // Use centralized auto-save helper (non-blocking)
              autoSaveAndUpload('tier_limit_reached').catch((err) => {
                console.error('[TierLimit] Auto-save failed:', err);
                // Only show alert if upload ACTUALLY failed
                alert(`⚠️ Recording stopped but upload failed.\n\nYour recording is saved locally and will retry automatically.\n\nError: ${err.message}`);
              });

              return newTime;
            }

            // Show warnings for approaching limits
            duringCheck.warnings.forEach(warning => {
              if (warning.percentage >= 90) {
                console.warn('Recording limit warning:', warning.message);
              }
            });
          }

          return newTime;
        });
      }, 1000);
      
      // Start browser MediaRecorder
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      // PHASE 2: Per-chunk upload with retry logic (GUARDED BY KILL SWITCH)
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          const chunkBlob = event.data;

          // KILL SWITCH: If chunking disabled, just push to audioChunksRef for single-blob upload
          if (!CHUNKED_UPLOADS_ENABLED) {
            audioChunksRef.current.push(chunkBlob);

            // DISABLED: localStorage backup causes quota exceeded spam
            // Progressive backup is unnecessary because:
            // 1. We keep full recording in audioChunksRef (memory)
            // 2. Upload happens immediately on stop
            // 3. Retry logic handles failures (lines 546-606)
            // 4. localStorage quota (5-10MB) is too small for 2-hour recordings

            // RUTHLESS VOICE FIX: Backup audio to IndexedDB every 30 seconds
            // NOTE: Conservative backup approach - saves entire blob each time (not incremental)
            // This is space-heavy but provides maximum safety for P0. Could be optimized to
            // save only deltas in future (P1). QuotaExceededError triggers auto-upload as safety valve.
            if (currentBackupSessionRef.current && currentBackupSessionRef.current.id) {
              const now = Date.now();

              // Audio backup every 30 seconds (full blob for data safety)
              if (now - lastMetadataBackupRef.current > 30000 && audioChunksRef.current.length > 0) {
                try {
                  const currentAudioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

                  await VoiceBackupService.addChunk(
                    currentBackupSessionRef.current.id,
                    currentAudioBlob,
                    Math.floor(recordingTime / 1000)
                  );

                  console.log(`[AudioBackup] Saved ${(currentAudioBlob.size / 1024 / 1024).toFixed(2)}MB to IndexedDB (full blob)`);
                  lastMetadataBackupRef.current = now;
                } catch (backupError) {
                  // Check for quota exceeded error (storage pressure)
                  if (backupError.name === 'QuotaExceededError') {
                    console.warn('[Storage Pressure] IndexedDB quota exceeded, triggering auto-upload');
                    autoSaveAndUpload('storage_pressure');
                  } else {
                    console.warn('[AudioBackup] Failed:', backupError);
                  }
                }
              }
            }

            return;
          }

          // ==================== CHUNKED UPLOAD CODE (DISABLED) ====================
          // This code is kept for future re-enable when kill switch is turned off
          const currentSeq = chunkSequenceRef.current;
          const recordingId = recordingIdRef.current;

          console.log(`[Chunk Upload] Starting upload - seq=${currentSeq}, size=${(chunkBlob.size / 1024 / 1024).toFixed(2)}MB`);

          // Idempotency check: skip if already uploaded
          if (uploadedChunksRef.current.includes(currentSeq)) {
            console.log(`[Chunk Upload] Skipping seq=${currentSeq} - already uploaded`);
            chunkSequenceRef.current++;
            return;
          }

          // Upload chunk with 3-retry logic (2s, 4s, 8s backoff)
          // CRITICAL: Track upload promise to prevent race condition in finalization
          const uploadPromise = (async () => {
            let uploadSuccess = false;
            for (let attempt = 0; attempt < 3; attempt++) {
              try {
                if (attempt > 0) {
                  const delayMs = attempt * 2000; // 2s, 4s
                  console.log(`[Chunk Upload] Retry ${attempt}/3 after ${delayMs}ms delay`);
                  await new Promise(resolve => setTimeout(resolve, delayMs));
                }

                const token = await currentUser.getIdToken();
                const response = await axios.post('/api/voice/upload-chunk', chunkBlob, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'audio/webm',
                    'x-recording-id': recordingId,
                    'x-sequence-number': currentSeq.toString(),
                    'x-is-final': 'false',
                    ...(projectId && { 'x-project-id': projectId })
                  }
                });

                if (response.data.success) {
                  uploadedChunksRef.current.push(currentSeq);
                  uploadSuccess = true;

                  // Show checkpoint toast every 10 minutes
                  const currentTime = Math.floor(recordingTime / 1000);
                  if (currentTime > 0 && currentTime % 600 === 0 && currentTime !== lastCheckpointTimeRef.current) {
                    const minutes = Math.floor(currentTime / 60);
                    console.log(`✓ Checkpoint saved at ${minutes}:00`);
                    // TODO: Show toast UI notification
                    lastCheckpointTimeRef.current = currentTime;
                  }

                  console.log(`[Chunk Upload] Success - seq=${currentSeq}, chunkId=${response.data.chunkId}`);
                  break;
                }
              } catch (error) {
                console.warn(`[Chunk Upload] Attempt ${attempt + 1}/3 failed:`, error.message);
                if (attempt === 2) {
                  console.error(`[Chunk Upload] All retries failed for seq=${currentSeq}`);
                  // Continue recording even if chunk upload fails - finalize will handle recovery
                }
              }
            }
          })();

          pendingChunkUploadsRef.current.add(uploadPromise);
          uploadPromise.finally(() => {
            pendingChunkUploadsRef.current.delete(uploadPromise);
          });
          await uploadPromise;

          // Increment sequence for next chunk
          chunkSequenceRef.current++;

          // PHASE B: Progressive backup during recording (bulletproof data protection)
          audioChunksRef.current.push(chunkBlob);
          if (currentBackupSessionRef.current && currentBackupSessionRef.current.id) {
            try {
              await VoiceBackupService.addChunk(
                currentBackupSessionRef.current.id,
                chunkBlob,
                10 * 60 * 1000  // 10-minute chunks
              );
            } catch (backupError) {
              console.warn('Chunk backup failed:', backupError);
              // Continue recording even if backup fails
            }
          }
        }
      };

      // PHASE 2: Generate unique recording ID (kept for compatibility)
      const recordingId = `rec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      recordingIdRef.current = recordingId;
      chunkSequenceRef.current = 0;
      uploadedChunksRef.current = [];
      lastCheckpointTimeRef.current = 0;
      pendingChunkUploadsRef.current.clear(); // Reset pending uploads tracker

      // KILL SWITCH: Use 1-second timeslice for local backup, NOT for chunk uploads
      console.log(`[Recording] Starting recording - recordingId=${recordingId}, chunking=${CHUNKED_UPLOADS_ENABLED ? 'enabled' : 'DISABLED'}`);
      mediaRecorder.start(1000); // 1-second chunks for local backup (chunking disabled)

      // PHASE 2: Tab close safety - flush last chunk on visibility change
      const handleVisibilityChange = () => {
        if (document.hidden && isRecording) {
          console.log('[Visibility] Tab hidden while recording - auto-saving');
          autoSaveAndUpload('tab_hidden');
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // PHASE 1: beforeunload safety - prevent data loss on browser close
      const handleBeforeUnload = (e) => {
        if (isRecording || audioChunksRef.current.length > 0) {
          // Save to backup immediately
          autoSaveAndUpload('tab_closing');

          // Show browser warning (standard browsers)
          e.preventDefault();
          e.returnValue = 'You have an unsaved recording. Are you sure you want to leave?';
          return e.returnValue;
        }
      };

      // PHASE 1: pagehide safety - iOS Safari doesn't support beforeunload reliably
      const handlePageHide = (e) => {
        if (isRecording || audioChunksRef.current.length > 0) {
          console.log('[PageHide] Saving recording before page unloads');

          // CRITICAL: Use synchronous save for guaranteed delivery on iOS
          // (regular async operations may be cancelled during page unload)
          autoSaveAndUpload('page_hiding');
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('pagehide', handlePageHide);

      // Store cleanup functions for later
      mediaRecorder._cleanupVisibilityListener = () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
      mediaRecorder._cleanupUnloadListeners = () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('pagehide', handlePageHide);
      };

      // Start browser speech recognition for real-time preview (if enabled)
      if (enableRealTimeTranscription && 'webkitSpeechRecognition' in window) {
        const SpeechRecognition = window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        // CORRECTED: Track which results we've already processed
        let finalTranscript = '';
        let lastFinalIndex = -1;  // Track the last final result we've processed

        recognition.onresult = (event) => {
          let interimTranscript = '';

          // Process only NEW final results (not already processed ones)
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal && i > lastFinalIndex) {
              // This is a new final result we haven't processed yet
              finalTranscript += result[0].transcript + ' ';
              lastFinalIndex = i;  // Update our tracking index
            } else if (!result.isFinal) {
              // Always include current interim results
              interimTranscript += result[0].transcript;
            }
          }

          // Show accumulated final text + current interim text. The ref mirrors
          // the same value synchronously so stopRecording can read the latest
          // transcript even before React commits the next render.
          const fullTranscript = finalTranscript + interimTranscript;
          setCurrentTranscript(fullTranscript);
          realtimeTranscriptRef.current = fullTranscript;
        };

        recognition.onerror = (event) => {
          console.warn('[Recognition] Error:', event.error);
          if (event.error === 'no-speech') {
            // Auto-restart on no speech to keep recognition active
            setTimeout(() => {
              if (recognitionRef.current && isRecording) {
                try {
                  recognition.start();
                } catch (e) {
                  console.warn('[Recognition] Restart failed:', e);
                }
              }
            }, 1000);
          }
        };

        recognition.onend = () => {
          // Flag set so stopRecording's wait-for-end can resolve.
          recognitionEndedRef.current = true;

          // Auto-restart recognition if still recording
          if (isRecording && enableRealTimeTranscription) {
            try {
              setTimeout(() => {
                if (recognitionRef.current) {
                  // Reset tracking variables for clean restart
                  lastFinalIndex = -1;
                  recognitionEndedRef.current = false;
                  recognition.start();
                }
              }, 100);
            } catch (e) {
              console.warn('[Recognition] Auto-restart failed:', e);
            }
          }
        };

        recognitionRef.current = recognition;
        recognition.start();
      }
      
    } catch (error) {
      console.error('Recording start failed:', error);
      setIsRecording(false);
      setConnectionStatus('error');
    }
  };

  // Auto-save and upload helper - centralized logic for all auto-save scenarios
  const autoSaveAndUpload = async (reason) => {
    console.log(`[AutoSave] Triggered by: ${reason}`);

    if (!isRecording && !isStopping) {
      console.log('[AutoSave] Not recording, skipping');
      return null;
    }

    // CRITICAL: Save to backup FIRST before attempting upload
    if (currentBackupSessionRef.current && audioChunksRef.current.length > 0) {
      try {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await VoiceBackupService.completeBackup(
          currentBackupSessionRef.current.id,
          audioBlob,
          currentTranscript
        );
        console.log(`[AutoSave] Backup saved to IndexedDB (${(audioBlob.size / 1024 / 1024).toFixed(2)}MB)`);
      } catch (backupError) {
        console.error('[AutoSave] Backup failed:', backupError);
        // Continue with upload attempt anyway
      }
    }

    // Attempt upload
    try {
      const result = await stopRecording({ autoUpload: true });
      console.log(`[AutoSave] Upload success: voiceId=${result.voiceId}`);
      return result;
    } catch (error) {
      console.error(`[AutoSave] Upload failed: ${error.message}`);
      // Data is safe in IndexedDB backup, user can recover via VoiceRecovery UI
      return null;
    }
  };

  const stopRecording = async (opts = { autoUpload: true }) => {
    // console.log('[Recording] stopRecording called - sessionRef:', currentBackupSessionRef.current?.id); // DEBUG: Verify session on stop

    // CRITICAL: Prevent multiple simultaneous stops
    if (!mediaRecorderRef.current || isStopping) {
      return { blob: null, voiceId: lastVoiceId };
    }

    // GPT5 FIX: Generate upload token to prevent race condition
    const uploadToken = uuidv4();
    setPendingUploadToken(uploadToken);
    console.log('[Recording] Generated upload token:', uploadToken);

    // IMMEDIATELY set stopping state - UI reflects this instantly
    setIsStopping(true);
    setIsRecording(false);  // UI shows "stopping" immediately
    setConnectionStatus('stopping');
    
    // Stop timer and speech recognition immediately
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }

    // Tell webkitSpeechRecognition to stop. The browser still delivers any
    // in-flight final results asynchronously and then fires onend. We wait
    // briefly for that flag below (in the realtime snapshot) so we don't
    // lose the tail of the user's speech.
    recognitionEndedRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.warn('[Recognition] stop() threw:', e?.message || e);
        recognitionEndedRef.current = true;
      }
    } else {
      recognitionEndedRef.current = true; // No recognition was active
    }
    
    const recorder = mediaRecorderRef.current;
    
    return new Promise(async (resolve, reject) => {
      try {
        // STEP 1: Wait for MediaRecorder to actually stop
        const stopped = new Promise((stopResolve, stopReject) => {
          recorder.onstop = stopResolve;
          recorder.onerror = (e) => stopReject(e.error || e);
          
          // Attempt to stop the recorder
          if (recorder.state !== 'inactive') {
            recorder.stop();
          } else {
            stopResolve(); // Already stopped
          }
        });
        
        // Wait for actual stop
        await stopped;

        // STEP 2: Finalize audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        // GPT5 FIX: Compute duration once, reuse for all upload paths
        const actualDurationSec = await ensureDuration(audioBlob);
        console.log('[Recording] Will use duration:', actualDurationSec, 'seconds for all uploads');

        // Wait briefly for webkitSpeechRecognition to finish flushing any
        // in-flight final results after stop() was called. onend (set above
        // in recognition.onend) flips recognitionEndedRef. We bound the wait
        // at 500ms so a flaky browser can't stall the upload.
        const waitForRecognitionEnd = new Promise((endResolve) => {
          if (recognitionEndedRef.current) {
            endResolve();
            return;
          }
          const startedAt = Date.now();
          const tick = () => {
            if (recognitionEndedRef.current || Date.now() - startedAt > 500) {
              endResolve();
            } else {
              setTimeout(tick, 25);
            }
          };
          tick();
        });
        await waitForRecognitionEnd;

        // Snapshot the realtime transcript from the ref (synchronous mirror
        // of currentTranscript that's updated inside recognition.onresult).
        // We can't read from React state here because the upload-stream
        // response will overwrite currentTranscript with the server's
        // (possibly hallucinated) version via setCurrentTranscript on the
        // next render.
        const realtimeSnapshot = (realtimeTranscriptRef.current || currentTranscript || '').trim();

        // PHASE B: Complete backup session with final data
        if (currentBackupSessionRef.current && currentBackupSessionRef.current.id) {
          try {
            await VoiceBackupService.completeBackup(
              currentBackupSessionRef.current.id,
              audioBlob,
              currentTranscript
            );
            setCurrentBackupSession(null);
            currentBackupSessionRef.current = null;
          } catch (backupError) {
            console.error('Failed to complete backup session:', backupError);
            // Continue with upload even if backup completion fails
          }
        }

        // STEP 3: Handle upload if requested
        let voiceId = null;
        if (opts.autoUpload && audioBlob.size > 0) {
          setConnectionStatus('processing');

          // KILL SWITCH: Skip chunk finalization, always use single-blob upload
          if (!CHUNKED_UPLOADS_ENABLED) {
            console.log('[Voice] Using single-blob upload (chunking disabled)', {
              size: audioBlob?.size,
              type: audioBlob?.type,
              projectId: projectId
            });

            // CRITICAL: Save transcript to localStorage IMMEDIATELY for recovery (no audio - prevents QuotaExceededError)
            try {
              const backupData = {
                // audio: omitted intentionally to prevent localStorage quota errors
                timestamp: Date.now(),
                transcript: currentTranscript || '',
                projectId: projectId,
                size: audioBlob.size,
                duration: recordingTime
              };
              localStorage.setItem('voice_backup_latest', JSON.stringify(backupData));
              console.log('[simple-voice] Backup saved (transcript only):', backupData.transcript.substring(0, 50) + '...');
            } catch (backupError) {
              console.error('[simple-voice] Failed to backup transcript:', backupError);
              // Non-fatal - continue with upload
            }

            try {
              // CRITICAL: Check network status first
              if (!navigator.onLine) {
                console.error('[Voice] Offline — saving recording locally');
                alert('⚠️ You are offline. Recording saved locally and will sync when back online.');
                setConnectionStatus('error');
                setIsStopping(false);
                reject(new Error('Offline'));
                return;
              }

              let token;
              try {
                token = await currentUser.getIdToken();
              } catch (tokenError) {
                console.error('[Voice] Firebase token failed:', tokenError);
                alert('⚠️ Authentication failed (likely network issue). Recording saved locally.');
                setConnectionStatus('error');
                setIsStopping(false);
                reject(tokenError);
                return;
              }

              // KILL SWITCH: Always use single-blob upload (skip finalization)
              console.log('[Voice] Uploading single blob to /api/voice/upload-stream', {
                size: audioBlob?.size,
                type: audioBlob?.type,
                projectId: projectId,
                durationSec: actualDurationSec  // GPT5 FIX: Already calculated above
              });

              const response = await axios.post('/api/voice/upload-stream', audioBlob, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'audio/webm',
                  'x-filename': 'recording.webm',
                  'x-duration-sec': actualDurationSec.toString(),  // GPT5 FIX: Use pre-calculated duration
                  ...(projectId && { 'x-project-id': projectId })
                },
                timeout: 0, // Unlimited timeout for large files
                onUploadProgress: (progressEvent) => {
                  if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    if (percentCompleted % 10 === 0) { // Log every 10%
                      console.log(`[Upload Progress] ${percentCompleted}% (${(progressEvent.loaded / 1024 / 1024).toFixed(1)}MB / ${(progressEvent.total / 1024 / 1024).toFixed(1)}MB)`);
                    }
                  }
                }
              });

              voiceId = response.data.voiceId || response.data.noteId;
              const transcript = response.data.transcript;
              setLastVoiceId(voiceId);
              setCurrentTranscript(transcript || currentTranscript);

              // GPT5 FIX: Record usage with actual duration, not timer
              const usageInfo = TierLimitService.recordUsage(userTier, actualDurationSec, audioBlob.size);
              if (usageInfo) {
                console.log(`Usage recorded: ${usageInfo.minutesUsed}min (${usageInfo.remainingThisMonth}min remaining)`);
              }

              // Clear backup on successful upload
              localStorage.removeItem('voice_backup_latest');

              // Persist realtime transcript as the server-side fallback for Whisper hallucinations.
              sendRealtimeTranscript(voiceId, realtimeSnapshot);

              // GPT5 FIX: Use uploadSucceeded() with token to prevent race condition
              uploadSucceeded(voiceId, uploadToken);

            } catch (uploadError) {
              console.error('[Upload] Initial upload failed:', uploadError);

              // AUTO-RETRY: 3 attempts with exponential backoff (2s, 4s, 8s)
              let uploadSuccess = false;
              for (let attempt = 1; attempt <= 3; attempt++) {
                console.log(`[Retry ${attempt}/3] Retrying upload in ${attempt * 2}s...`);
                await new Promise(resolve => setTimeout(resolve, attempt * 2000));

                try {
                  // Re-fetch token for retry to ensure it's fresh
                  const retryToken = await currentUser.getIdToken();
                  const retryResponse = await axios.post('/api/voice/upload-stream', audioBlob, {
                    headers: {
                      'Authorization': `Bearer ${retryToken}`,
                      'Content-Type': 'audio/webm',
                      'x-filename': 'recording.webm',
                      'x-duration-sec': actualDurationSec.toString(),  // GPT5 FIX: Use pre-calculated duration
                      ...(projectId && { 'x-project-id': projectId })
                    },
                    timeout: 0,
                    onUploadProgress: (e) => {
                      if (e.total && e.loaded === e.total) {
                        console.log(`[Retry ${attempt}] Upload complete`);
                      }
                    }
                  });

                  voiceId = retryResponse.data.voiceId || retryResponse.data.noteId;
                  const transcript = retryResponse.data.transcript;
                  setLastVoiceId(voiceId);
                  setCurrentTranscript(transcript || currentTranscript);

                  uploadSuccess = true;

                  // Clear backup on success
                  localStorage.removeItem('voice_backup_latest');
                  console.log(`[Retry ${attempt}] Upload succeeded!`);

                  // GPT5 FIX: Record usage with actual duration, not timer
                  const usageInfo = TierLimitService.recordUsage(userTier, actualDurationSec, audioBlob.size);
                  if (usageInfo) {
                    console.log(`Usage recorded: ${usageInfo.minutesUsed}min (${usageInfo.remainingThisMonth}min remaining)`);
                  }

                  // Persist realtime transcript as the server-side fallback for Whisper hallucinations.
                  sendRealtimeTranscript(voiceId, realtimeSnapshot);

                  // GPT5 FIX: Use uploadSucceeded() with token to prevent race condition
                  uploadSucceeded(voiceId, uploadToken);

                  setConnectionStatus('disconnected');
                  setIsStopping(false);

                  // Stop all media tracks
                  if (recorder.stream) {
                    recorder.stream.getTracks().forEach(track => track.stop());
                  }

                  resolve({ blob: audioBlob, voiceId: voiceId });
                  return; // Exit early on success

                } catch (retryError) {
                  console.warn(`[Retry ${attempt}] Failed:`, retryError.message);
                  // Continue to next attempt
                }
              }

              // All retries failed - save locally and alert user
              if (!uploadSuccess) {
                console.error('[Upload] All retry attempts failed');

                // CRITICAL: Save transcript at minimum if upload fails
                const emergencyTranscript = currentTranscript || 'Voice recording failed to upload';
                const failureData = {
                  transcript: emergencyTranscript,
                  timestamp: Date.now(),
                  error: uploadError.message,
                  audioSize: audioBlob.size,
                  duration: recordingTime,
                  projectId: projectId
                };

                // Store failed upload for recovery
                const existingFailedUploads = JSON.parse(localStorage.getItem('voice_failed_uploads') || '[]');
                existingFailedUploads.push(failureData);
                localStorage.setItem('voice_failed_uploads', JSON.stringify(existingFailedUploads));

                // Alert user about the failure
                alert(`⚠️ VOICE UPLOAD FAILED!\n\nRetried 3 times but upload failed.\n\nYour transcript has been saved locally:\n"${emergencyTranscript.substring(0, 100)}..."\n\nPlease check your internet connection and try again. Your recording is backed up locally.`);

                setConnectionStatus('error');
                setIsStopping(false);
                reject(uploadError);
                return;
              }
            }
          }

          // ==================== CHUNKED UPLOAD FINALIZATION (DISABLED) ====================
          // This code is kept for future re-enable when kill switch is turned off
          // Wrapped in if (CHUNKED_UPLOADS_ENABLED) block below

          if (CHUNKED_UPLOADS_ENABLED) {

            // CRITICAL BARRIER: Wait for all pending chunk uploads to complete before finalization
            console.log(`[Finalize] Waiting for ${pendingChunkUploadsRef.current.size} pending chunk uploads...`);
            await Promise.all([...pendingChunkUploadsRef.current]);
            console.log('[Finalize] All pending chunks uploaded');

            const recordingId = recordingIdRef.current;
            const totalChunks = uploadedChunksRef.current.length;

            // CRITICAL: Save transcript to localStorage IMMEDIATELY for recovery (no audio - prevents QuotaExceededError)
            try {
              const backupData = {
                // audio: omitted intentionally to prevent localStorage quota errors
                timestamp: Date.now(),
                transcript: currentTranscript || '',
                projectId: projectId,
                size: audioBlob.size,
                duration: recordingTime
              };
              localStorage.setItem('voice_backup_latest', JSON.stringify(backupData));
              console.log('[simple-voice] Backup saved (transcript only):', backupData.transcript.substring(0, 50) + '...');
            } catch (backupError) {
              console.error('[simple-voice] Failed to backup transcript:', backupError);
              // Non-fatal - continue with upload
            }

            try {
              // CRITICAL: Check network status first
              if (!navigator.onLine) {
                console.error('[Voice] Offline — saving recording locally');
                alert('⚠️ You are offline. Recording saved locally and will sync when back online.');
                setConnectionStatus('error');
                setIsStopping(false);
                reject(new Error('Offline'));
                return;
              }

              let token;
              try {
                token = await currentUser.getIdToken();
              } catch (tokenError) {
                console.error('[Voice] Firebase token failed:', tokenError);
                alert('⚠️ Authentication failed (likely network issue). Recording saved locally.');
                setConnectionStatus('error');
                setIsStopping(false);
                reject(tokenError);
                return;
              }

              // PHASE 2: If we have chunks, finalize the recording
              if (recordingId && totalChunks > 0) {
              console.log(`[Finalize] Calling finalize-recording - recordingId=${recordingId}, totalChunks=${totalChunks}`);

              try {
                // Upload final chunk if there's remaining data
                if (audioChunksRef.current.length > uploadedChunksRef.current.length) {
                  const finalChunkSeq = chunkSequenceRef.current;
                  const finalChunkBlob = audioChunksRef.current[audioChunksRef.current.length - 1];

                  console.log(`[Finalize] Uploading final chunk - seq=${finalChunkSeq}, size=${(finalChunkBlob.size / 1024 / 1024).toFixed(2)}MB`);

                  const finalChunkResponse = await axios.post('/api/voice/upload-chunk', finalChunkBlob, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'audio/webm',
                      'x-recording-id': recordingId,
                      'x-sequence-number': finalChunkSeq.toString(),
                      'x-is-final': 'true',
                      ...(projectId && { 'x-project-id': projectId })
                    }
                  });

                  // Track final chunk upload
                  if (finalChunkResponse.data.success && !finalChunkResponse.data.idempotent) {
                    uploadedChunksRef.current.push(finalChunkSeq);
                  }
                }

                // OFF-BY-ONE GUARD: Use actual uploaded chunk count, not seq+1
                const actualTotalChunks = uploadedChunksRef.current.length;
                console.log(`[Finalize] Uploaded chunks: ${uploadedChunksRef.current.join(', ')} (total: ${actualTotalChunks})`);
                console.log(`[Finalize] rid=${recordingId} chunks=${actualTotalChunks}`);

                // Call finalize-recording endpoint
                const finalizeResponse = await axios.post('/api/voice/finalize-recording', {
                  totalChunks: actualTotalChunks
                }, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-recording-id': recordingId,
                    ...(projectId && { 'x-project-id': projectId })
                  },
                  timeout: 120000 // 2 minutes for ffmpeg processing
                });

                voiceId = finalizeResponse.data.voiceId;
                setLastVoiceId(voiceId);
                console.log(`[Finalize] Success - voiceId=${voiceId}, chunksProcessed=${finalizeResponse.data.chunksProcessed}`);

                // Clear backup on success
                localStorage.removeItem('voice_backup_latest');

                // GPT5 FIX: Record usage with actual duration
                const usageInfo = TierLimitService.recordUsage(userTier, actualDurationSec, audioBlob.size);
                if (usageInfo) {
                  console.log(`Usage recorded: ${usageInfo.minutesUsed}min (${usageInfo.remainingThisMonth}min remaining)`);
                }

                // GPT5 FIX: Use uploadSucceeded() with token to prevent race condition
                uploadSucceeded(voiceId, uploadToken);

              } catch (finalizeError) {
                console.error('[Finalize] Failed, falling back to single-blob upload:', finalizeError.message);

                // FALLBACK: Use single-blob upload if finalize fails
                console.log('[Fallback] Using /api/voice/upload-stream', {
                  size: audioBlob?.size,
                  type: audioBlob?.type,
                  projectId: projectId
                });

                const fallbackResponse = await axios.post('/api/voice/upload-stream', audioBlob, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'audio/webm',
                    'x-filename': 'recording.webm',
                    'x-duration-sec': actualDurationSec.toString(),  // GPT5 FIX: Use pre-calculated duration
                    ...(projectId && { 'x-project-id': projectId })
                  },
                  timeout: 0,
                  onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                      if (percentCompleted % 10 === 0) {
                        console.log(`[Fallback Upload] ${percentCompleted}%`);
                      }
                    }
                  }
                });

                voiceId = fallbackResponse.data.voiceId || fallbackResponse.data.noteId;
                setLastVoiceId(voiceId);
                console.log('[Fallback] Upload succeeded');

                // GPT5 FIX: Record usage with actual duration
                TierLimitService.recordUsage(userTier, actualDurationSec, audioBlob.size);

                // GPT5 FIX: Use uploadSucceeded() with token to prevent race condition
                uploadSucceeded(voiceId, uploadToken);
              }
            } else {
              // No chunks - use original single-blob upload
              console.log('[Voice] About to POST /api/voice/upload-stream', {
                size: audioBlob?.size,
                type: audioBlob?.type,
                projectId: projectId
              });

              // PHASE B: Use streaming upload endpoint with raw audio blob
              const response = await axios.post('/api/voice/upload-stream', audioBlob, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'audio/webm',
                'x-filename': 'recording.webm',
                'x-duration-sec': actualDurationSec.toString(),  // GPT5 FIX: Use pre-calculated duration
                ...(projectId && { 'x-project-id': projectId })
              },
              timeout: 0, // Unlimited timeout (was 60000) - large files on mobile need time
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  if (percentCompleted % 10 === 0) { // Log every 10% for better visibility
                    console.log(`[Upload Progress] ${percentCompleted}% (${(progressEvent.loaded / 1024 / 1024).toFixed(1)}MB / ${(progressEvent.total / 1024 / 1024).toFixed(1)}MB)`);
                  }
                }
              }
            });

              voiceId = response.data.voiceId || response.data.noteId;
              const transcript = response.data.transcript;
              setLastVoiceId(voiceId);
              setCurrentTranscript(transcript || currentTranscript);

              // GPT5 FIX: Record usage with actual duration, not timer
              const usageInfo = TierLimitService.recordUsage(userTier, actualDurationSec, audioBlob.size);
              if (usageInfo) {
                console.log(`Usage recorded: ${usageInfo.minutesUsed}min (${usageInfo.remainingThisMonth}min remaining)`);
              }

              // Clear backup on successful upload
              localStorage.removeItem('voice_backup_latest');

              // Persist realtime transcript as the server-side fallback for Whisper hallucinations.
              sendRealtimeTranscript(voiceId, realtimeSnapshot);

              // GPT5 FIX: Use uploadSucceeded() with token to prevent race condition
              uploadSucceeded(voiceId, uploadToken);
            }
          } catch (chunkedUploadError) {
            console.error('[Chunked Upload] Error in chunked upload path:', chunkedUploadError);
            setConnectionStatus('error');
            setIsStopping(false);
            reject(chunkedUploadError);
            return;
          }
          // End of if (CHUNKED_UPLOADS_ENABLED) block
        }

        // STEP 4: Clean up and resolve (shared by all upload paths)
        setConnectionStatus('disconnected');
        setIsStopping(false);

        // Stop all media tracks
        if (recorder.stream) {
          recorder.stream.getTracks().forEach(track => track.stop());
        }

        // Clean up visibility listener
        if (recorder._cleanupVisibilityListener) {
          recorder._cleanupVisibilityListener();
        }

        // Clean up unload listeners
        if (recorder._cleanupUnloadListeners) {
          recorder._cleanupUnloadListeners();
        }

        resolve({ blob: audioBlob, voiceId: voiceId || lastVoiceId });
      }        

      } catch (error) {
        console.error('Recording stop failed:', error);
        setConnectionStatus('error');
        setIsStopping(false);
        reject(error);
      }
    });
  };
  
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // GPT5 FIX: Calculate audio duration from blob with Safari quirk fix
  const ensureDuration = async (blob) => {
    try {
      const d = await new Promise((resolve, reject) => {
        const audio = new Audio();
        const url = URL.createObjectURL(blob);

        audio.addEventListener('loadedmetadata', () => {
          const duration = audio.duration;
          URL.revokeObjectURL(url);
          resolve(duration);
        });

        audio.addEventListener('error', (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        });

        audio.src = url;
        // Safari quirk fix: force load after setting src
        setTimeout(() => audio.load(), 0);
      });

      const fromTimer = Math.max(1, Math.floor(recordingTime / 1000));
      const sec = Number.isFinite(d) && d > 0 ? Math.round(d) : fromTimer;
      setLastActualDurationSec(sec);
      console.log(`[Duration] Calculated: ${sec}s (timer: ${fromTimer}s, blob: ${Math.round(d)}s)`);
      return sec;
    } catch (error) {
      console.warn('[Duration] Failed to read audio metadata, using timer fallback:', error);
      const fromTimer = Math.max(1, Math.floor(recordingTime / 1000));
      setLastActualDurationSec(fromTimer);
      return fromTimer;
    }
  };

  // Fire-and-forget POST that persists the browser-captured realtime transcript
  // after the audio upload completes. The server uses this as a fallback when
  // Whisper hallucinates on long/quiet audio (see backend/utils/whisperHallucinationDetector.js).
  // Non-blocking — failure here is non-fatal because the audio + Whisper run
  // independently. We just lose the realtime fallback for that note.
  const sendRealtimeTranscript = async (voiceId, transcriptText) => {
    if (!voiceId || !transcriptText || transcriptText.trim().length === 0) {
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        `/api/voice/${voiceId}/realtime-transcript`,
        { transcript: transcriptText.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        }
      );
      console.log(`[Realtime] Persisted realtime transcript (${transcriptText.trim().length} chars) for ${voiceId}`);
    } catch (err) {
      console.warn('[Realtime] Failed to persist realtime transcript (non-fatal):', err?.message || err);
    }
  };

  // GPT5 FIX: Safe upload success handler with race guard
  const uploadSucceeded = (voiceId, token) => {
    try {
      onVoiceUploaded?.(voiceId);
    } catch (err) {
      console.error('[uploadSucceeded] onVoiceUploaded callback error:', err);
    }

    // Only clear if this success corresponds to the last stopRecording call
    setTimeout(() => {
      setPendingUploadToken(current => {
        if (current && current === token) {
          console.log('[uploadSucceeded] Token match, clearing session');
          clearSession(); // includes setProjectId(null) ✅
          return null;
        }
        console.log('[uploadSucceeded] Token mismatch or cleared, not clearing session (new recording started)');
        return current; // someone started a new recording; don't clear
      });
    }, 0);
  };

  // Mock functions for compatibility (not implemented in simple version)
  const pauseRecording = () => setIsPaused(true);
  const resumeRecording = () => setIsPaused(false);
  const clearSession = () => {
    setSessionId(null);
    setCurrentTranscript('');
    setRecordingTime(0);
    setProjectId(null);  // FIX #1A: Clear projectId to prevent leaking between recordings
    setLastActualDurationSec(null);  // Also clear duration
    console.log('[Recording] Session cleared, projectId reset');
  };

  const contextValue = {
    // Recording state
    isRecording,
    isStopping,        // CRITICAL: New state for UI
    isPaused,
    sessionId,
    recordingTime,
    currentTranscript,
    connectionStatus,
    lastVoiceId,       // CRITICAL: New state for voice ID tracking
    projectId,         // Project context

    // PHASE B: Bulletproof backup and limits
    currentBackupSession,
    tierLimitsCheck,
    userTier,
    tierLoading,
    refetchTier,
    
    // Recording controls
    startRecording,
    stopRecording,
    toggleRecording,
    pauseRecording,
    resumeRecording,
    clearSession,
    setProjectId,      // Set project context
    
    // Real-time transcription
    enableRealTimeTranscription,
    setEnableRealTimeTranscription,
    
    // Mock values for compatibility
    isLiveEnabled: true,
    maxDurationMs: 7200000, // 2 hours - no practical limit
    chunks: [],
    currentChunkIndex: 0,
    totalChunks: 0,
    uploadProgress: {},
    
    // Mock functions
    getAudioBlob: () => {
      if (audioChunksRef.current.length > 0) {
        return new Blob(audioChunksRef.current, { type: 'audio/webm' });
      }
      return null;
    }
  };

  return (
    <RecordingContext.Provider value={contextValue}>
      {children}
    </RecordingContext.Provider>
  );
}